import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import {
  Hourglass, Store, UserPlus, Plus, Search, Loader2, AlertTriangle, MessageSquare, Send, Trash2,
  CheckCircle2, X, Truck, PackageCheck, PackageX, ExternalLink, RefreshCw, StickyNote, ChevronDown,
  Globe, CalendarClock, Tag, Hash,
} from 'lucide-react'

const PS_ORDER_URL = 'https://www.jvs-informatica.com/admin058mixixqnew/index.php/sell/orders/{id}/view'

interface Comment {
  id: number
  item_key: string
  text: string
  author: string
  author_name: string | null
  created_at: string
}

interface PendingPart {
  key: string
  origin: 'prestashop' | 'manual'
  origin_label: string
  status: 'rosa' | 'contrarreembolso' | 'manual'
  id: number | null
  reference: string
  description: string
  supplier_code: string | null
  supplier_name: string | null
  supplier_reference: string | null
  stock: number
  supplier_orders: { order: number; units: number }[]
  registered: boolean
  supply: SupplyAlloc[]
  supply_status: 'en_stock' | 'pedido_proveedor' | 'pedido_web' | 'sin_pedir'
  uncovered_units: number
  eta: SupplyAlloc | null
  units: number
  id_order: number | null
  order_reference: string | null
  customer: string | null
  product_name: string | null
  date: string
  days_waiting: number
  note: string | null
  created_by: string | null
  comments: Comment[]
}

interface SupplyAlloc {
  type: 'stock' | 'supplier_order' | 'web_order'
  units: number
  supplier_order?: number
  web_order?: string
  order_date?: string
  supplier_code?: string
  supplier_name?: string | null
  supplier_doc?: string | null
  arrival_from?: string
  arrival_to?: string
  delivery_from?: string
  delivery_to?: string
  late?: boolean
}

interface SearchResult {
  reference: string | null
  supplier_reference: string | null
  supplier_code: string | null
  name: string
  registered: boolean
  stock: number | null
}

interface PendingPartsResponse {
  catalog_suppliers: { code: string; name: string | null }[]
  items: PendingPart[]
  totals: { all: number; rosa: number; contrarreembolso: number; manual: number }
  suppliers: { code: string; name: string | null }[]
  generated_at: string
  current_user: string
  is_admin: boolean
}

type OriginFilter = 'all' | 'rosa' | 'contrarreembolso' | 'manual'

// Estilo por origen: PrestaShop (rosa pagado / ámbar contrarreembolso) frente a manual (índigo)
const STYLE = {
  rosa: {
    stripe: 'bg-pink-500',
    badge: 'bg-pink-100 text-pink-700 ring-pink-200 dark:bg-pink-900/40 dark:text-pink-300 dark:ring-pink-800',
    card: 'border-pink-200/70 dark:border-pink-900/50',
    label: 'Pagado · pendiente de stock',
  },
  contrarreembolso: {
    stripe: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-800',
    card: 'border-amber-200/70 dark:border-amber-900/50',
    label: 'Contrarreembolso · en espera de pago',
  },
  manual: {
    stripe: 'bg-gradient-to-b from-indigo-500 to-violet-500',
    badge: 'bg-indigo-100 text-indigo-700 ring-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300 dark:ring-indigo-800',
    card: 'border-indigo-200/70 dark:border-indigo-900/50 bg-gradient-to-r from-indigo-50/60 to-transparent dark:from-indigo-950/30',
    label: 'Añadida a mano',
  },
} as const

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?'

const fmtDate = (s: string) => `${s.slice(8, 10)}/${s.slice(5, 7)}${s.length > 10 ? ' ' + s.slice(11, 16) : ''}`

function Avatar({ name, className = '' }: { name: string; className?: string }) {
  return (
    <span className={`inline-flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-semibold ${className}`}>
      {initials(name)}
    </span>
  )
}

function AgeBadge({ days }: { days: number }) {
  const cls = days >= 15
    ? 'bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300'
    : days >= 7
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
      : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`} title="Días esperando">
      <Hourglass className="w-3 h-3" />
      {days === 0 ? 'hoy' : `${days} d`}
    </span>
  )
}

const shortName = (n?: string | null) => (n || '').split(/[\s/]+/)[0] || 'proveedor'
const range = (a?: string, b?: string) => {
  if (!a || !b) return ''
  return a === b ? fmtDate(a) : `${fmtDate(a)} – ${fmtDate(b)}`
}

function SupplyStatus({ part }: { part: PendingPart }) {
  const cfg = {
    en_stock: { icon: PackageCheck, text: 'En stock', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
    pedido_proveedor: { icon: Truck, text: 'Pedida al proveedor', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
    pedido_web: { icon: Globe, text: 'Pedida en web (sin factura)', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
    sin_pedir: { icon: PackageX, text: 'Sin pedir al proveedor', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  }[part.supply_status]
  const late = part.eta?.late && part.supply_status !== 'en_stock'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${late ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : cfg.cls}`}>
      <cfg.icon className="w-3 h-3" /> {late ? 'Con retraso' : cfg.text}
    </span>
  )
}

// Situación explicada para atender al cliente («¿cómo va mi pedido?»)
function SupplyPanel({ part }: { part: PendingPart }) {
  const lines = part.supply.map((a, i) => {
    if (a.type === 'stock') {
      return (
        <div key={i} className="flex items-start gap-2 text-emerald-800 dark:text-emerald-300">
          <PackageCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <span><b>{a.units} ud en stock</b> en el almacén: lista para preparar y enviar.</span>
        </div>
      )
    }
    const isWeb = a.type === 'web_order'
    return (
      <div key={i} className={`flex items-start gap-2 ${a.late ? 'text-amber-800 dark:text-amber-300' : isWeb ? 'text-sky-800 dark:text-sky-300' : 'text-blue-800 dark:text-blue-300'}`}>
        {isWeb ? <Globe className="w-4 h-4 mt-0.5 shrink-0" /> : <Truck className="w-4 h-4 mt-0.5 shrink-0" />}
        <span>
          <b>{a.units} ud {isWeb ? `pedida${a.units === 1 ? '' : 's'} en la web de ${shortName(a.supplier_name)}` : `en el pedido a ${shortName(a.supplier_name)} nº ${a.supplier_order}`}</b>
          {' '}del {fmtDate(a.order_date || '')}
          {isWeb && <span className="opacity-80"> (pedido web {a.web_order}, pendiente de su factura)</span>}
          {!isWeb && a.supplier_doc && <span className="opacity-70"> · {a.supplier_doc}</span>}
          {a.late ? (
            <span className="block mt-0.5 font-medium">
              <AlertTriangle className="inline w-3.5 h-3.5 -mt-0.5" /> Debía llegar como tarde el {fmtDate(a.arrival_to || '')} y aún no ha entrado: conviene consultar al proveedor.
            </span>
          ) : (
            <span className="block mt-0.5">
              <CalendarClock className="inline w-3.5 h-3.5 -mt-0.5" /> Llegada al almacén aprox. <b>{range(a.arrival_from, a.arrival_to)}</b>
              {' '}· entrega al cliente aprox. <b>{range(a.delivery_from, a.delivery_to)}</b>
            </span>
          )}
        </span>
      </div>
    )
  })
  return (
    <div className="mt-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/40 px-3 py-2 space-y-1.5 text-sm">
      {lines}
      {part.uncovered_units > 0 && (
        <div className="flex items-start gap-2 text-slate-600 dark:text-slate-300">
          <PackageX className="w-4 h-4 mt-0.5 shrink-0" />
          <span><b>{part.uncovered_units} ud sin pedir</b> todavía al proveedor: saldrá{part.uncovered_units === 1 ? '' : 'n'} en el próximo pedido{part.supplier_name ? ` a ${shortName(part.supplier_name)}` : ''}.</span>
        </div>
      )}
    </div>
  )
}

function CommentsThread({ part, currentUser, isAdmin }: { part: PendingPart; currentUser: string; isAdmin: boolean }) {
  const qc = useQueryClient()
  const [textValue, setTextValue] = useState('')
  const add = useMutation({
    mutationFn: async () => api.post('/reports/pending-parts/comments', { key: part.key, text: textValue }),
    onSuccess: () => { setTextValue(''); qc.invalidateQueries({ queryKey: ['pending-parts'] }) },
  })
  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/reports/pending-parts/comments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-parts'] }),
  })

  return (
    <div className="mt-3 pt-3 border-t border-slate-200/80 dark:border-slate-700 space-y-3">
      {part.comments.length === 0 && (
        <p className="text-xs text-slate-400 italic">Sin comentarios todavía.</p>
      )}
      {part.comments.map((c) => (
        <div key={c.id} className="flex gap-2.5 group">
          <Avatar name={c.author_name || c.author} className="w-7 h-7 text-[11px] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{c.author_name || c.author}</span>
              <span className="text-[11px] text-slate-400">{fmtDate(c.created_at)}</span>
              {(c.author === currentUser || isAdmin) && (
                <button
                  onClick={() => { if (confirm('¿Borrar este comentario?')) del.mutate(c.id) }}
                  className="ml-auto opacity-0 group-hover:opacity-100 text-slate-400 hover:text-danger-600 transition-opacity"
                  title="Borrar comentario"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap break-words">{c.text}</p>
          </div>
        </div>
      ))}
      <form
        onSubmit={(e) => { e.preventDefault(); if (textValue.trim()) add.mutate() }}
        className="flex gap-2"
      >
        <input
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          placeholder="Añadir un comentario…"
          className="flex-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
        />
        <button
          type="submit"
          disabled={!textValue.trim() || add.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {add.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Enviar
        </button>
      </form>
    </div>
  )
}

function PartCard({ part, currentUser, isAdmin }: { part: PendingPart; currentUser: string; isAdmin: boolean }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const [reason, setReason] = useState('')
  const st = STYLE[part.status]
  const close = useMutation({
    mutationFn: async () => api.post(`/reports/pending-parts/${part.id}/close`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-parts'] }),
  })

  return (
    <div className={`relative overflow-hidden rounded-xl border bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-shadow ${st.card}`}>
      <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${st.stripe}`} />
      <div className="pl-5 pr-4 py-3.5">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
          {/* Origen */}
          <div className="w-44 shrink-0">
            {part.origin === 'prestashop' ? (
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ring-1 ${st.badge}`}>
                <Store className="w-3.5 h-3.5" /> PrestaShop
              </span>
            ) : (
              <span className={`inline-flex items-center gap-1.5 pl-1 pr-2.5 py-0.5 rounded-lg text-xs font-semibold ring-1 ${st.badge}`}>
                <Avatar name={part.origin_label} className="w-5 h-5 text-[9px]" />
                {part.origin_label}
              </span>
            )}
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{st.label}</p>
          </div>

          {/* Pieza */}
          <div className="flex-1 min-w-[16rem]">
            <div className="flex flex-wrap items-baseline gap-x-2">
              {part.registered ? (
                <>
                  <span className="font-mono text-sm font-semibold text-indigo-600 dark:text-indigo-400">{part.reference}</span>
                  {part.supplier_reference && (
                    <span className="font-mono text-xs text-slate-500 dark:text-slate-400">({part.supplier_reference})</span>
                  )}
                </>
              ) : (
                <>
                  <span className="font-mono text-sm font-semibold text-slate-700 dark:text-slate-200">{part.supplier_reference}</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" title="Producto sin alta en Factusol: se pide por la referencia del proveedor">
                    sin alta
                  </span>
                </>
              )}
              <span className="text-xs text-slate-400">· {part.supplier_name || 'sin proveedor'}</span>
            </div>
            <p className="text-sm text-slate-800 dark:text-slate-200 line-clamp-1" title={part.description || part.product_name || ''}>
              {part.description || part.product_name}
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-500 dark:text-slate-400">
              {part.id_order ? (
                <a
                  href={PS_ORDER_URL.replace('{id}', String(part.id_order))}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-slate-700 dark:text-slate-300 hover:text-indigo-600"
                >
                  Pedido {part.id_order} <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span className="italic">sin pedido asociado</span>
              )}
              {part.customer && <span>{part.customer}</span>}
              <span>{part.origin === 'manual' ? 'añadida' : 'pedido'} el {fmtDate(part.date)}</span>
            </div>
          </div>

          {/* Estado */}
          <div className="flex flex-col items-end gap-1.5 ml-auto">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-slate-900 dark:text-white">{part.units}</span>
              <span className="text-xs text-slate-500">ud{part.units === 1 ? '' : 's'}</span>
              <AgeBadge days={part.days_waiting} />
            </div>
            <SupplyStatus part={part} />
          </div>
        </div>

        <SupplyPanel part={part} />

        {part.note && (
          <div className="mt-2.5 flex gap-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 px-3 py-2 text-sm text-indigo-900 dark:text-indigo-200">
            <StickyNote className="w-4 h-4 mt-0.5 shrink-0 text-indigo-400" />
            <span className="whitespace-pre-wrap">{part.note}</span>
          </div>
        )}

        <div className="flex items-center gap-2 mt-2.5">
          <button
            onClick={() => setOpen((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
              part.comments.length
                ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {part.comments.length ? `${part.comments.length} comentario${part.comments.length === 1 ? '' : 's'}` : 'Comentar'}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {part.comments.length > 0 && !open && (
            <span className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-md">
              <b className="font-medium">{part.comments[part.comments.length - 1].author_name || part.comments[part.comments.length - 1].author}:</b>{' '}
              {part.comments[part.comments.length - 1].text}
            </span>
          )}
          {part.origin === 'manual' && !closing && (
            <button
              onClick={() => setClosing(true)}
              className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
              title="La pieza ya se ha enviado o ya no hace falta: deja de contar en los pedidos a proveedor"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Cerrar
            </button>
          )}
        </div>

        {closing && (
          <form
            onSubmit={(e) => { e.preventDefault(); close.mutate() }}
            className="mt-2.5 flex flex-wrap items-center gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 p-2.5"
          >
            <input
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo (opcional): enviada, anulada por el cliente…"
              className="flex-1 min-w-[14rem] px-3 py-1.5 text-sm border border-emerald-300 dark:border-emerald-800 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
            <button type="submit" disabled={close.isPending} className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
              {close.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cerrar pieza'}
            </button>
            <button type="button" onClick={() => setClosing(false)} className="px-2 py-1.5 text-sm text-slate-500 hover:text-slate-800">
              Cancelar
            </button>
          </form>
        )}

        {open && <CommentsThread part={part} currentUser={currentUser} isAdmin={isAdmin} />}
      </div>
    </div>
  )
}

function AddPartModal({ onClose, catalogSuppliers }: { onClose: () => void; catalogSuppliers: { code: string; name: string | null }[] }) {
  const qc = useQueryClient()
  const [mode, setMode] = useState<'jvs' | 'supplier'>('jvs')
  const [supplier, setSupplier] = useState(catalogSuppliers.find((c) => c.code === '23')?.code || catalogSuppliers[0]?.code || '')
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [selected, setSelected] = useState<SearchResult | null>(null)
  const [freeName, setFreeName] = useState('')
  const [units, setUnits] = useState('1')
  const [order, setOrder] = useState('')
  const [note, setNote] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 350)
    return () => clearTimeout(t)
  }, [q])

  const search = useQuery({
    queryKey: ['pending-parts-search', mode, supplier, debounced],
    enabled: debounced.length >= 2 && !selected,
    queryFn: async () => {
      const res = await api.get<{ results: SearchResult[] }>('/reports/pending-parts/search', {
        params: { q: debounced, mode, ...(mode === 'supplier' ? { supplier } : {}) },
      })
      return res.data.results
    },
  })

  const switchMode = (m: 'jvs' | 'supplier') => { setMode(m); setSelected(null); setQ(''); setFreeName('') }
  const supplierName = catalogSuppliers.find((c) => c.code === supplier)?.name || ''
  // en modo proveedor se puede usar una referencia que no esté en el catálogo («tal cual», sin alta)
  const freeRef = mode === 'supplier' && !selected && q.trim().length >= 2 ? q.trim() : ''

  const create = useMutation({
    mutationFn: async () => {
      const base = { units: Number(units), id_order: order ? Number(order) : null, note: note || null }
      if (selected?.registered && selected.reference) {
        return api.post('/reports/pending-parts', { ...base, reference: selected.reference })
      }
      return api.post('/reports/pending-parts', {
        ...base,
        supplier_code: supplier,
        supplier_reference: selected ? selected.supplier_reference : freeRef,
        product_name: selected ? selected.name : freeName,
      })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pending-parts'] }); onClose() },
  })
  const err = (create.error as { response?: { data?: { detail?: string } } } | null)?.response?.data?.detail
  const canSubmit = !!selected || (!!freeRef && freeName.trim().length > 2)

  const inputCls = 'w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-slate-800 shadow-2xl overflow-hidden max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            <h2 className="font-semibold">Añadir pieza pendiente</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) create.mutate() }} className="p-5 space-y-4 overflow-y-auto">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Para piezas que un cliente espera y PrestaShop no ve (por ejemplo, un envío parcial). Contará en el
            pedido al proveedor hasta que la cierres.
          </p>

          {/* Tipo de referencia */}
          <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-900">
            {([['jvs', 'Referencia JVS', Hash], ['supplier', 'Referencia de proveedor', Tag]] as const).map(([m, label, Icon]) => (
              <button
                key={m}
                type="button"
                onClick={() => switchMode(m)}
                className={`inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === m ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>

          {mode === 'supplier' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Proveedor *</label>
              <select value={supplier} onChange={(e) => { setSupplier(e.target.value); setSelected(null) }} className={inputCls}>
                {catalogSuppliers.map((c) => <option key={c.code} value={c.code}>{c.name || c.code}</option>)}
              </select>
            </div>
          )}

          {/* Buscador / seleccionado */}
          {selected ? (
            <div className="flex items-start gap-3 rounded-xl border-2 border-indigo-300 dark:border-indigo-700 bg-indigo-50/70 dark:bg-indigo-950/40 p-3">
              <PackageCheck className="w-5 h-5 mt-0.5 text-indigo-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  {selected.registered && selected.reference ? (
                    <span className="font-mono font-semibold text-indigo-700 dark:text-indigo-300">{selected.reference}</span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-amber-100 text-amber-800">sin alta</span>
                  )}
                  {selected.supplier_reference && <span className="font-mono text-sm text-slate-500">({selected.supplier_reference})</span>}
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-200">{selected.name}</p>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700" title="Cambiar producto">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Buscar producto por {mode === 'jvs' ? 'referencia JVS o nombre' : `referencia de ${shortName(supplierName)} o nombre`} *
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder={mode === 'jvs' ? 'PANT-INT-043, Xiaomi Air…' : '1-SCR0065, 81337, E480…'}
                  className={`${inputCls} pl-10`}
                />
                {search.isFetching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-indigo-500" />}
              </div>
              {debounced.length >= 2 && (search.data || []).length > 0 && (
                <ul className="mt-2 max-h-60 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
                  {(search.data || []).map((r, i) => (
                    <li key={`${r.reference}-${r.supplier_reference}-${i}`}>
                      <button
                        type="button"
                        onClick={() => setSelected(r)}
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors"
                      >
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          {r.reference ? (
                            <span className="font-mono text-sm font-semibold text-indigo-600 dark:text-indigo-400">{r.reference}</span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-amber-100 text-amber-800">sin alta</span>
                          )}
                          {r.supplier_reference && <span className="font-mono text-xs text-slate-500">{r.reference ? `(${r.supplier_reference})` : r.supplier_reference}</span>}
                          {r.stock != null && mode === 'supplier' && (
                            <span className="ml-auto text-[11px] text-slate-400">stock prov. {r.stock}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-1">{r.name}</p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {debounced.length >= 2 && !search.isFetching && (search.data || []).length === 0 && (
                <p className="mt-2 text-sm text-slate-500">No hay resultados{mode === 'jvs' ? '. Si no está dado de alta, búscalo por referencia de proveedor.' : ' en el catálogo.'}</p>
              )}
              {freeRef && (
                <div className="mt-3 rounded-lg border border-dashed border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/20 p-3 space-y-2">
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    ¿No aparece? Puedes añadir <b className="font-mono">{freeRef}</b> tal cual, como producto sin alta de {shortName(supplierName)}. Escribe su nombre:
                  </p>
                  <input value={freeName} onChange={(e) => setFreeName(e.target.value)} placeholder="Nombre del producto" className={inputCls} />
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Unidades *</label>
              <input type="number" min="1" step="1" required value={units} onChange={(e) => setUnits(e.target.value)} className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nº de pedido PrestaShop</label>
              <input type="number" value={order} onChange={(e) => setOrder(e.target.value)} placeholder="135926 (opcional)" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Observaciones</label>
            <textarea
              rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Ej.: se enviaron las baterías el 28/09; falta la pantalla"
              className={inputCls}
            />
          </div>
          {err && (
            <div className="flex items-center gap-2 rounded-lg bg-danger-50 dark:bg-danger-900/30 px-3 py-2 text-sm text-danger-700 dark:text-danger-300">
              <AlertTriangle className="w-4 h-4" /> {err}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">
              Cancelar
            </button>
            <button type="submit" disabled={!canSubmit || create.isPending} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50">
              {create.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Añadir
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function PendingPartsPage() {
  const [supplier, setSupplier] = useState('')
  const [origin, setOrigin] = useState<OriginFilter>('all')
  const [search, setSearch] = useState('')
  const [showAdd, setShowAdd] = useState(false)

  const query = useQuery({
    queryKey: ['pending-parts', supplier],
    queryFn: async () => {
      const res = await api.get<PendingPartsResponse>('/reports/pending-parts', { params: supplier ? { supplier } : {} })
      return res.data
    },
  })
  const data = query.data

  const items = useMemo(() => {
    const q = search.trim().toUpperCase()
    return (data?.items || []).filter((p) => {
      if (origin !== 'all' && p.status !== origin) return false
      if (!q) return true
      return [p.reference, p.supplier_reference, p.description, p.customer, p.id_order ? String(p.id_order) : '', p.origin_label]
        .some((v) => (v || '').toUpperCase().includes(q))
    })
  }, [data, origin, search])

  const cards: { key: OriginFilter; label: string; value: number; icon: typeof Store; cls: string }[] = [
    { key: 'all', label: 'Todas', value: data?.totals.all ?? 0, icon: Hourglass, cls: 'from-slate-600 to-slate-800' },
    { key: 'rosa', label: 'PrestaShop · pagados', value: data?.totals.rosa ?? 0, icon: Store, cls: 'from-pink-500 to-rose-600' },
    { key: 'contrarreembolso', label: 'PrestaShop · contrarreembolso', value: data?.totals.contrarreembolso ?? 0, icon: Store, cls: 'from-amber-400 to-orange-500' },
    { key: 'manual', label: 'Añadidas a mano', value: data?.totals.manual ?? 0, icon: UserPlus, cls: 'from-indigo-500 to-violet-600' },
  ]

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="p-2 bg-pink-100 dark:bg-pink-900/40 rounded-lg">
            <Hourglass className="w-8 h-8 text-pink-600 dark:text-pink-400" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Piezas pendientes</h1>
            <p className="text-slate-500 dark:text-slate-400">
              Piezas que esperan stock para un cliente. Todas entran solas en los pedidos a proveedor.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <RefreshCw className={`w-4 h-4 ${query.isFetching ? 'animate-spin' : ''}`} /> Actualizar
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-medium shadow-sm hover:shadow-md transition-shadow"
          >
            <Plus className="w-4 h-4" /> Añadir pieza pendiente
          </button>
        </div>
      </div>

      {/* Tarjetas-filtro por origen */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <button
            key={c.key}
            onClick={() => setOrigin(c.key)}
            className={`text-left rounded-xl p-4 text-white bg-gradient-to-br ${c.cls} shadow-sm transition-all ${
              origin === c.key ? 'ring-4 ring-offset-2 ring-indigo-300 dark:ring-offset-slate-900 scale-[1.02]' : 'opacity-90 hover:opacity-100'
            }`}
          >
            <div className="flex items-center justify-between">
              <c.icon className="w-5 h-5 text-white/80" />
              <span className="text-3xl font-bold">{query.isLoading ? '…' : c.value}</span>
            </div>
            <p className="text-sm mt-2 text-white/90">{c.label}</p>
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
        <select
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
          className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
        >
          <option value="">Todos los proveedores</option>
          {(data?.suppliers || []).map((s) => (
            <option key={s.code} value={s.code}>{s.name || s.code}</option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[14rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar referencia, ref. proveedor, pedido, cliente o usuario…"
            className="w-full pl-10 pr-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          />
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-pink-500" /> Pagado</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500" /> Contrarreembolso (≤40 días)</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-b from-indigo-500 to-violet-500" /> Manual</span>
        </div>
      </div>

      {/* Listado */}
      {query.isLoading ? (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          <p className="text-slate-500">Cargando piezas pendientes…</p>
        </div>
      ) : query.isError ? (
        <div className="flex flex-col items-center gap-3 py-16">
          <AlertTriangle className="w-8 h-8 text-danger-500" />
          <p className="text-danger-600">Error al cargar las piezas pendientes</p>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-slate-500">
          <PackageCheck className="w-10 h-10 text-emerald-500" />
          <p>No hay piezas pendientes con estos filtros</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <PartCard key={p.key} part={p} currentUser={data!.current_user} isAdmin={data!.is_admin} />
          ))}
        </div>
      )}

      {showAdd && <AddPartModal onClose={() => setShowAdd(false)} catalogSuppliers={data?.catalog_suppliers || []} />}
    </div>
  )
}
